import { GenericResponse } from '@/common/common_utils';
import { RoundButton } from '@/components/buttons/Buttons';
import { ENDPOINTS } from '@/constants/endpoints';
import { ConciergeLiveMap } from '@/modules/concierge/map/ConciergeLiveMap';
import { fetchActivityRouteData } from '@/modules/concierge/map/useInitialRoute';
import type { GoogleMapsRoutingResponse } from '@/modules/map/engine/GoogleTypes';
import type { GetOrderDetailsResponse } from '@/modules/vehicle/engine/ActivityTypes';
import type { X4bActivityDto } from '@/modules/vehicle/engine/X4bActivityDto';
import { getVehicleTypeInfo } from '@/modules/vehicle/engine/VehicleType';
import { axiosInstance } from '@/services/axios.service';
import { Avatar } from 'primereact/avatar';
import { Timeline } from 'primereact/timeline';
import { classNames } from 'primereact/utils';
import moment from 'moment';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { FaCircleDot, FaLocationDot } from 'react-icons/fa6';
import { MdStar } from 'react-icons/md';
import style from '../../../views/employee.module.css';
import RideFareBreakDownModal from './RideFareBreakDownModal';

type Props = {
	transaction: Transaction;
	activity?: X4bActivityDto | null;
};

type Transaction = {
	id: string;
	pickup: string;
	pickupAddress: string;
	dropoff: string;
	dropoffAddress: string;
	fare: number;
	status: string;
	duration: string;
	distance: string;
	paymentMethod: string;
	bookingMethod: string;
	driverName: string;
	driverRating: number;
	carDetails: string;
	carPlate: string;
};

interface RideTimeline {
	name?: string;
	address?: string;
	icon?: ReactNode;
}

const OrderDetails = (props: Props) => {
	const { transaction, activity } = props;

	const [showRideFareBreakdown, setShowRideFareBreakdown] = useState(false);
	const [routingData, setRoutingData] = useState<GoogleMapsRoutingResponse | null>(null);

	const tripIdSetterRef = useRef<(tripId: string) => void>(undefined);
	const bindTripIdSetter = useCallback((setter: (tripId: string) => void) => {
		tripIdSetterRef.current = setter;
	}, []);

	const orderUuidSetterRef = useRef<(orderUuid: string) => void>(undefined);
	const bindOrderUuidSetter = useCallback((setter: (orderUuid: string) => void) => {
		orderUuidSetterRef.current = setter;
	}, []);

	const [orderDetails, setOrderDetails] = useState<GetOrderDetailsResponse | null>(null);

	useEffect(() => {
		if (!activity?.XpressId) return;

		fetchActivityRouteData(
			activity,
			activity.Id,
			activity.XpressId,
			setRoutingData,
			orderUuidSetterRef,
			tripIdSetterRef,
			true
		);

		axiosInstance
			.post<GenericResponse<GetOrderDetailsResponse>>(
				ENDPOINTS['/api/X4bActivity/GetOrderDetails'],
				{ BusinessOrderId: activity.XpressId },
			)
			.then((res) => setOrderDetails(res.data.Data ?? null))
			.catch(() => setOrderDetails(null));
	}, [activity?.XpressId, activity?.Id, activity?.Status]);

	const formatDistance = (distanceInM?: number) => {
		if (!distanceInM) return '-';
		const km = distanceInM / 1000;
		return `${Number.isInteger(km) ? km.toFixed(0) : km.toFixed(1)}km`;
	};

	const pb = orderDetails?.PriceBreakdown;
	const rideFare = pb
		? {
			base: pb.BaseFare,
			distance: formatDistance(pb.DistanceInM),
			fee: pb.DistanceFare,
			time: pb.TimeFare,
			surgeCharges: pb.SurgeCharges,
			promo: pb.PromoAmount ?? 0,
			total: pb.TotalAmount,
		}
		: {
			base: 0,
			distance: transaction.distance,
			fee: 0,
			time: 0,
			surgeCharges: 0,
			promo: 0,
			total: transaction.fare,
		};

	const rideTimeline: RideTimeline[] = [
		{
			name: transaction.pickup,
			address: transaction.pickupAddress,
			icon: <FaCircleDot className="text-primary" size={20} />,
		},
		{
			name: transaction.dropoff,
			address: transaction.dropoffAddress,
			icon: <FaLocationDot className="text-error" size={20} />,
		},
	];

	const customizedMarker = (item: RideTimeline) => {
		return <div className="my-1">{item.icon}</div>;
	};

	const customizedContent = (item: RideTimeline) => {
		return (
			<div>
				<p className="text-body-medium text-onPrimaryContainer">
					{item.name}
				</p>
				<p className="text-body-small text-neutral60">{item.address}</p>
			</div>
		);
	};

	const listTemplate = (label: string, value: string) => (
		<div>
			<p className="font-semibold text-title-medium">{value}</p>
			<p className="text-label-medium text-neutral60">{label}</p>
		</div>
	);

	let rideTime = transaction.duration || '-';
	if (activity?.PickedUpTime && activity?.CompletedTime) {
		const duration = moment.duration(moment(activity.CompletedTime).diff(activity.PickedUpTime));
		rideTime = `${String(Math.floor(duration.asHours())).padStart(2, '0')}:${String(duration.minutes()).padStart(2, '0')}:${String(duration.seconds()).padStart(2, '0')}`;
	}

	const rideDistance = activity?.DistanceInM
		? `${Math.round(activity.DistanceInM / 1000)}km`
		: transaction.distance || '-';

	const vehicleImagePath = activity
		? getVehicleTypeInfo(activity.VehicleType)?.imageLargePath
		: undefined;

	return (
		<>
			<div className="space-y-4">
				{/* Pickup & Dropoff */}
				<div className="pl-6">
					<Timeline
						value={rideTimeline}
						marker={customizedMarker}
						content={customizedContent}
						className="w-full"
						align="left"
						pt={{
							content: {
								className: 'font-poppins',
							},
							opposite: {
								className: 'hidden',
							},
							connector: {
								className: style.employeeTimelineConnector,
							},
						}}
					/>
				</div>
				<div className="space-y-4 pl-6">
					{/* Fare & Status */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						<div className="flex justify-between">
							{listTemplate(
								'Ride Fare',
								`₱${transaction.fare.toFixed(2)}`,
							)}

							<RoundButton
								label="View"
								id={'view'}
								className="secondary"
								onClick={() => setShowRideFareBreakdown(true)}
							/>
						</div>
						{listTemplate('Trip Status', transaction.status)}
					</div>

					{/* Duration & Distance */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						{listTemplate('Trip Duration', rideTime)}
						{listTemplate('Trip Distance', rideDistance)}
					</div>

					{/* Payment & Booking Method */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						{listTemplate(
							'Payment Method',
							'Business Account'
							//transaction.paymentMethod,
						)}
						{listTemplate(
							'Booking Method',
							transaction.bookingMethod,
						)}
					</div>
				</div>

				{/* Map */}
				{activity ? (
					<ConciergeLiveMap
						key={activity.XpressId}
						bindTripIdSetter={bindTripIdSetter}
						bindOrderUuidSetter={bindOrderUuidSetter}
						pickupLocationName={activity.Pickup?.PlaceName || activity.Pickup?.Address || undefined}
						dropOffLocationName={activity.Dropoff?.PlaceName || activity.Dropoff?.Address || undefined}
						mapDivClassName="w-full h-[296px] rounded-lg"
						activity={activity}

						initialRoute={routingData}
						showCompletedRoute={true}
					/>
				) : (
					<img
						alt="map"
						width={792}
						height={296}
						src={'/images/svg/map-overview.svg'}
						className="w-full"
					/>
				)}

				{/* Driver Info */}
				<div className="grid grid-cols-2 pl-6">
					<div className="flex flex-col self-center">
						<div className="flex items-center gap-2 text-title-medium font-semibold">
							{transaction.driverName} <MdStar />
							{transaction.driverRating?.toFixed(1)}
						</div>
						<div className="text-neutral60 text-title-medium">
							{transaction.carPlate} - {transaction.carDetails}
						</div>
					</div>
					<div className="flex items-center justify-end">
						<Avatar
							shape="circle"
							icon="pi pi-user"
							className={classNames(
								style.employeeAvatar,
								'-mr-8 z-50',
							)}
						/>
						<img
							alt="car"
							width={186}
							height={196}
							src={vehicleImagePath ?? '/images/svg/real-car.svg'}
						/>
					</div>
				</div>
			</div>
			<RideFareBreakDownModal
				visible={showRideFareBreakdown}
				onHide={() => setShowRideFareBreakdown(false)}
				rideFare={rideFare}
			/>
		</>
	);
};

export default OrderDetails;
